import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { formatDateTime } from "../../shared/formatting/dateTimes";
import { useLoad } from "../../shared/loading/useLoad";
import EmptyState from "../../ui/feedback/EmptyState";
import LoadStatus from "../../ui/feedback/LoadStatus";
import { FORM_DIVIDED_LIST_CLASS, FORM_SECTION_HEADING_CLASS } from "../../ui/styles/formStyles";
import { readProviderSpend, type ProviderSpend } from "./serverKeysApi";

/**
 * The providers' own spend this month (PR-10): read with the admin keys an administrator set
 * up as files, monitoring only. A reading that failed says why; it is never shown as zero.
 */
export default function ProviderSpendSection() {
  const { t } = useTranslation();
  const load = useCallback((signal: AbortSignal) => readProviderSpend(signal), []);
  const { state, reload } = useLoad(load);
  return (
    <section className="space-y-3">
      <h2 className={FORM_SECTION_HEADING_CLASS}>{t("serverKeys.spend.title")}</h2>
      <LoadStatus state={state} loadingLabel={t("common.loading")} onRetry={reload} />
      {state.status === "loaded" ? (
        state.value.providers.length === 0 ? (
          <EmptyState>{t("serverKeys.spend.none")}</EmptyState>
        ) : (
          <ul className={FORM_DIVIDED_LIST_CLASS}>
            {state.value.providers.map((spend) => (
              <SpendRow key={spend.provider} spend={spend} />
            ))}
          </ul>
        )
      ) : null}
    </section>
  );
}

function SpendRow({ spend }: { spend: ProviderSpend }) {
  const { t, i18n } = useTranslation();
  const dollars = (amount: string) => new Intl.NumberFormat(i18n.language, { style: "currency", currency: "USD" }).format(Number(amount));
  const limit =
    spend.limitUsd !== null
      ? t(spend.limitSource === "stated" ? "serverKeys.spend.limitStated" : "serverKeys.spend.limit", { amount: dollars(spend.limitUsd) })
      : spend.limitProblem !== null
        ? t("serverKeys.spend.limitNotRead", { reason: spend.limitProblem })
        : t("serverKeys.spend.noLimit");
  return (
    <li className="space-y-0.5 py-2 text-sm">
      <p className="font-semibold">
        {t(`serverKeys.api.${spend.provider}`)} <span className="app-text-muted font-normal">· {spend.scope === "organization" ? t("serverKeys.spend.organization") : spend.scope}</span>
      </p>
      <p>
        {spend.spentUsd !== null
          ? t("serverKeys.spend.spent", { month: spend.month, amount: dollars(spend.spentUsd) })
          : t("serverKeys.spend.notRead", { reason: spend.spendProblem ?? "" })}
      </p>
      <p>{limit}</p>
      <p className="app-text-muted text-xs">{t("serverKeys.spend.readAt", { time: formatDateTime(spend.readAt, i18n.language) })}</p>
    </li>
  );
}
